import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Bell, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export default function NotificationsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const { data: notifs = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    enabled: !!user,
    refetchInterval: 60000,
  });

  const readAllMut = useMutation({
    mutationFn: async () => (await api.post("/notifications/read-all")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const readOneMut = useMutation({
    mutationFn: async (id) => (await api.post(`/notifications/${id}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  if (!user) return null;
  const unread = notifs.filter((n) => !n.read).length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-white/5 text-zinc-400"
          data-testid="notifications-bell"
          aria-label="Notifications"
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-emerald-500 text-black text-[10px] font-bold grid place-items-center"
              data-testid="notifications-count"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 bg-card border-white/10">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
          <span className="text-sm font-medium">Notifications</span>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-zinc-400"
              onClick={() => readAllMut.mutate()}
              data-testid="notifications-read-all"
            >
              <Check className="w-3 h-3 mr-1" /> Tout marquer lu
            </Button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {notifs.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">
              Aucune notification.
            </div>
          ) : (
            notifs.map((n) => (
              <Link
                key={n.id}
                to={n.link || (n.portfolio_slug ? `/portefeuilles-sideb/${n.portfolio_slug}` : "#")}
                onClick={() => {
                  if (!n.read) readOneMut.mutate(n.id);
                  setOpen(false);
                }}
                className={`block px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                  !n.read ? "bg-emerald-500/[0.03]" : ""
                }`}
                data-testid={`notif-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{n.title}</div>
                    <div className="text-xs text-zinc-400 truncate mt-0.5">{n.body}</div>
                    <div className="text-[10px] text-zinc-600 mt-1">
                      {n.created_at ? new Date(n.created_at).toLocaleString("fr-FR") : ""}
                    </div>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
